import React, { useState } from 'react'
import { Card, Grid, Button, CircularProgress } from '@mui/material'
import { TextValidator, ValidatorForm } from 'react-material-ui-form-validator'
import makeStyles from '@mui/styles/makeStyles';
import history from 'history.js'
import clsx from 'clsx'
import useAuth from 'app/hooks/useAuth'
import { Auth } from 'aws-amplify'
import {
  PasswordTextValidator,
  SetupMFADialog,
  VerifyMFACodeDialog,
} from 'app/components'
import { toast } from 'react-toastify'
import NewPasswordDialog from 'app/components/NewPasswordDialog/NewPasswordDialog'

const useStyles = makeStyles(({ palette, ...theme }) => ({
  cardHolder: {
    background: '#1A2038',
  },
  card: {
    maxWidth: 800,
    width: '100%',
    borderRadius: 12,
    margin: '1rem',
  },
  buttonProgress: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    marginTop: -12,
    marginLeft: -12,
  },
}))

const SignIn = () => {
  const [loading, setLoading] = useState(false)
  const [userInfo, setUserInfo] = useState({
    email: '',
    password: '',
  })
  const [message, setMessage] = useState('')
  const { signInWithEmailAndPassword } = useAuth()
  const [setupMFAInfo, setSetupMFAInfo] = useState(null) // { user, qrCode }
  const [signInMFAUser, setSignInMFAUser] = useState(null)
  const [newPasswordUser, setNewPasswordUser] = useState(null)

  const classes = useStyles()

  const handleChange = ({ target: { name, value } }) => {
    let temp = { ...userInfo }
    temp[name] = value
    setUserInfo(temp)
  }

  const handleSignInUser = async (user) => {
    try {
      setLoading(true)
      switch (user.challengeName) {
        case 'MFA_SETUP':
          const secret = await Auth.setupTOTP(user)
          const qrCode = `otpauth://totp/AWSCognito:${userInfo.email}?secret=${secret}&issuer=ArtemisAI`
          setSetupMFAInfo({ user, qrCode })
          break
        case 'SMS_MFA':
        case 'SOFTWARE_TOKEN_MFA':
          setSignInMFAUser(user)
          break
        case 'NEW_PASSWORD_REQUIRED':
          setNewPasswordUser(user)
          break;
        default:
          history.push('/')
      }
    } catch (e) {
      setMessage(e.message)
    } finally {
      setLoading(false)
    }
  }

  const handleFormSubmit = async (event) => {
    setLoading(true)
    setMessage("")
    try {
      const user = await signInWithEmailAndPassword(
        userInfo.email,
        userInfo.password
      )
      await handleSignInUser(user)
    } catch (e) {
      console.log(e)
      setMessage(e.message)
    } finally {
      setLoading(false)
    }
  }

  const onVerifyMFASuccess = async () => {
    if (signInMFAUser.challengeName === 'SOFTWARE_TOKEN_MFA') {
      history.push('/')
    } else {
      toast.success(
        'SMS is used to recover your TOTP only. Please re-setup your TOTP.'
      )
      try {
        const secret = await Auth.setupTOTP(signInMFAUser)
        const qrCode = `otpauth://totp/AWSCognito:${userInfo.email}?secret=${secret}&issuer=ArtemisAI`
        setSetupMFAInfo({ user: signInMFAUser, qrCode })
      } catch (error) {
        toast.error(error.message)
        Auth.signOut()
      }
      setSignInMFAUser(null)
    }
  }

  const onSetupMFASuccess = async () => {
    const { challengeName } = setupMFAInfo.user
    if (
      challengeName === 'SOFTWARE_TOKEN_MFA' ||
      challengeName === 'MFA_SETUP'
    ) {
      history.push('/')
    } else {
      toast.warning('Receiving your TOTP...')
      try {
        await Auth.setPreferredMFA(setupMFAInfo.user, 'TOTP')
        setSetupMFAInfo(null)
        toast.success('MFA setup successfully. Please sign in again.')
      } catch (error) {
        toast.error(error.message)
      }
      Auth.signOut()
    }
  }
  return (
    <div
      className={clsx(
        'flex justify-center items-center  min-h-full-screen',
        classes.cardHolder
      )}
    >
      <Card className={classes.card}>
        <Grid container>
          <Grid item lg={5} md={5} sm={5} xs={12}>
            <div className="p-8 flex justify-center items-center h-full">
              <img className="w-full" src="/assets/images/logo.svg" alt="" />
            </div>
          </Grid>
          <Grid item lg={7} md={7} sm={7} xs={12}>
            <div className="p-8 h-full bg-light-gray relative">
              <ValidatorForm onSubmit={handleFormSubmit}>
                <TextValidator
                  className="mb-6 w-full"
                  variant="outlined"
                  size="small"
                  label="Email"
                  onChange={handleChange}
                  type="email"
                  name="email"
                  value={userInfo.email || null} // workaround: label overlaps with the autofilled value in Chrome
                  validators={['required', 'isEmail']}
                  errorMessages={[
                    'this field is required',
                    'email is not valid',
                  ]}
                />
                <PasswordTextValidator
                  className="mb-6 w-full"
                  label="Password"
                  onChange={handleChange}
                  name="password"
                  value={userInfo.password || null} // workaround: label overlaps with the autofilled value in Chrome
                  validators={['required']}
                  errorMessages={['this field is required']}
                />
                {message && <p className="text-error">{message}</p>}
                <SetupMFADialog
                  open={!!setupMFAInfo}
                  onClose={() => setSetupMFAInfo(null)}
                  onSuccess={onSetupMFASuccess}
                  user={setupMFAInfo?.user}
                  qrCode={setupMFAInfo?.qrCode}
                />
                <VerifyMFACodeDialog
                  open={!!signInMFAUser}
                  onClose={() => {
                    setSignInMFAUser(null)
                  }}
                  onSuccess={onVerifyMFASuccess}
                  user={signInMFAUser}
                />
                <NewPasswordDialog
                  open={!!newPasswordUser}
                  onClose={() => {
                    setNewPasswordUser(null)
                  }}
                  onSuccess={(user) => {
                    setNewPasswordUser(null)
                    handleSignInUser(user)
                  }}
                  user={newPasswordUser}
                />
                <div className="flex flex-wrap items-center mb-4">
                  <div className="relative">
                    <Button
                      variant="contained"
                      color="primary"
                      disabled={loading}
                      type="submit"
                    >
                      Sign in
                    </Button>
                    {loading && (
                      <CircularProgress
                        size={24}
                        className={classes.buttonProgress}
                      />
                    )}
                  </div>
                </div>
                <Button
                  className="text-primary"
                  onClick={() => history.push('/forgot-password')}
                >
                  Forgot password?
                </Button>
              </ValidatorForm>
            </div>
          </Grid>
        </Grid>
      </Card>
    </div>
  )
}

export default SignIn
